import { useState } from 'react';
import {
  Building2,
  CalendarClock,
  CreditCard,
  Crown,
  Globe,
  Mail,
  Pencil,
  Phone,
  Users,
  X,
} from 'lucide-react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { currency } from '../../utils/formatters';

const statusTone = {
  ativo: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
  trial: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
  pendente: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
  atrasado: 'bg-red-500/15 text-red-300 border border-red-500/30',
  bloqueado: 'bg-red-500/15 text-red-300 border border-red-500/30',
  cancelado: 'bg-zinc-600/30 text-zinc-400 border border-zinc-600',
};

const paymentTone = {
  approved: 'text-emerald-300',
  credited: 'text-emerald-300',
  pending: 'text-amber-300',
  in_process: 'text-amber-300',
  rejected: 'text-red-300',
  cancelled: 'text-zinc-400',
  refunded: 'text-zinc-400',
};

const providerLabels = {
  mercado_pago: 'Mercado Pago',
  stripe: 'Stripe',
  manual: 'Manual',
};

const baseCalculoLabels = {
  mensalidade_equivalente: 'Mensalidade equivalente',
  valor_fixo: 'Valor fixo',
};

function formatDateBR(value) {
  if (!value) return '—';
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? `${String(value).split('T')[0]}T12:00:00` : value;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('pt-BR');
}

function formatMoney(value, moeda = 'BRL') {
  const code = String(moeda || 'BRL').toUpperCase();
  return currency(value, {
    currency: code,
    locale: code === 'BRL' ? 'pt-BR' : 'en-US',
  });
}

function StatusPill({ value }) {
  const key = String(value || '—').toLowerCase();
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone[key] || 'bg-zinc-700 text-zinc-200'}`}>
      {value || '—'}
    </span>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg bg-surface2 px-3 py-2">
      {icon ? <span className="mt-0.5 text-zinc-500">{icon}</span> : null}
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</p>
        <p className="truncate text-sm text-zinc-100" title={String(value ?? '')}>{value || '—'}</p>
      </div>
    </div>
  );
}

const tabs = [
  { id: 'geral', label: 'Visão geral', icon: Building2 },
  { id: 'financeiro', label: 'Financeiro', icon: CreditCard },
  { id: 'equipe', label: 'Equipe', icon: Users },
  { id: 'venda', label: 'Venda', icon: Crown },
];

export default function GDNClientDetailModal({ client, payments = [], onClose, onEdit }) {
  const [tab, setTab] = useState('geral');
  if (!client) return null;

  const assinatura = client.assinatura || {};
  const moeda = String(assinatura.moeda || 'BRL').toUpperCase();
  const clientPayments = payments
    .filter((payment) => payment.assinatura_id === assinatura.id || payment.oficina_id === client.oficina_id)
    .slice()
    .sort((a, b) => String(b.criado_em || b.created_at || '').localeCompare(String(a.criado_em || a.created_at || '')));
  const totalPago = clientPayments
    .filter((payment) => ['approved', 'credited'].includes(String(payment.status || '').toLowerCase()))
    .reduce((total, payment) => total + Number(payment.valor || payment.transaction_amount || 0), 0);

  return (
    <Modal open={Boolean(client)} onClose={() => { setTab('geral'); onClose?.(); }} title={client.nome_oficina || 'Detalhes do cliente'}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill value={assinatura.status_pagamento || client.status} />
          {assinatura.tipo_acesso === 'trial' && (
            <span className="rounded-full border border-sky-500/30 bg-sky-500/15 px-2.5 py-1 text-xs font-semibold text-sky-300">
              Teste grátis
            </span>
          )}
          <span className="rounded-full bg-zinc-700/60 px-2.5 py-1 text-xs font-semibold text-zinc-200">
            {assinatura.plano || 'Sem plano'}
          </span>
          <span className="rounded-full bg-zinc-700/60 px-2.5 py-1 text-xs font-semibold text-zinc-200">
            {formatMoney(assinatura.valor_mensal || 0, moeda)}/mês
          </span>
          {client.origem_venda === 'parceiro' && (
            <span className="rounded-full border border-orange-500/30 bg-orange-500/15 px-2.5 py-1 text-xs font-semibold text-orange-300">
              Indicação: {client.referral_code || 'parceiro'}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 border-b border-border pb-2">
          {tabs.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  tab === item.id ? 'bg-accent/20 text-accent' : 'text-zinc-400 hover:bg-surface2 hover:text-zinc-200'
                }`}
              >
                <Icon size={15} />
                {item.label}
              </button>
            );
          })}
        </div>

        {tab === 'geral' && (
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <InfoRow icon={<Building2 size={15} />} label="Responsável" value={client.responsavel} />
              <InfoRow icon={<Mail size={15} />} label="E-mail" value={client.email} />
              <InfoRow icon={<Phone size={15} />} label="Telefone" value={client.telefone} />
              <InfoRow icon={<Globe size={15} />} label="País" value={client.pais_codigo} />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <InfoRow icon={<CalendarClock size={15} />} label="Início da assinatura" value={formatDateBR(assinatura.data_inicio)} />
              <InfoRow icon={<CalendarClock size={15} />} label="Próximo vencimento" value={formatDateBR(assinatura.data_vencimento)} />
              <InfoRow
                icon={<CalendarClock size={15} />}
                label="Período de teste"
                value={
                  assinatura.trial_inicio || assinatura.trial_fim
                    ? `${formatDateBR(assinatura.trial_inicio)} até ${formatDateBR(assinatura.trial_fim)}`
                    : '—'
                }
              />
              <InfoRow
                icon={<CreditCard size={15} />}
                label="Provedor de pagamento"
                value={providerLabels[assinatura.provedor_pagamento] || assinatura.provedor_pagamento || '—'}
              />
            </div>
            {assinatura.bloqueado_em && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
                <p className="font-semibold">Assinatura bloqueada em {formatDateBR(assinatura.bloqueado_em)}</p>
                {assinatura.bloqueado_motivo ? (
                  <p className="mt-0.5 text-xs text-red-200/80">Motivo: {assinatura.bloqueado_motivo}</p>
                ) : null}
              </div>
            )}
            {client.ultimoPagamento && (
              <div className="rounded-lg border border-border bg-surface2 p-3">
                <p className="text-[11px] uppercase tracking-wide text-zinc-500">Último pagamento</p>
                <p className="mt-0.5 text-sm text-zinc-100">
                  {formatMoney(client.ultimoPagamento.valor || client.ultimoPagamento.transaction_amount || 0, client.ultimoPagamento.moeda || moeda)}
                  <span className="text-zinc-500">
                    {' '}· {formatDateBR(client.ultimoPagamento.criado_em || client.ultimoPagamento.created_at)} · {client.ultimoPagamento.status || '—'}
                  </span>
                </p>
              </div>
            )}
          </div>
        )}
        {tab === 'financeiro' && (
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="rounded-lg bg-surface2 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-zinc-500">Mensalidade</p>
                <p className="font-display text-lg text-zinc-100">{formatMoney(assinatura.valor_mensal || 0, moeda)}</p>
              </div>
              <div className="rounded-lg bg-surface2 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-zinc-500">Total pago</p>
                <p className="font-display text-lg text-emerald-300">{formatMoney(totalPago, moeda)}</p>
              </div>
              <div className="rounded-lg bg-surface2 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-zinc-500">Pagamentos</p>
                <p className="font-display text-lg text-zinc-100">{clientPayments.length}</p>
              </div>
            </div>
            {clientPayments.length === 0 ? (
              <p className="rounded-lg bg-surface2 p-3 text-sm text-zinc-400">Nenhum pagamento registrado para este cliente.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-surface2 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-3 py-2">Data</th>
                      <th className="px-3 py-2">Valor</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientPayments.slice(0, 12).map((payment) => {
                      const status = String(payment.status || '—').toLowerCase();
                      return (
                        <tr key={payment.id || `${payment.mercado_pago_payment_id}-${payment.criado_em}`} className="border-t border-border">
                          <td className="px-3 py-2 text-zinc-300">{formatDateBR(payment.criado_em || payment.created_at)}</td>
                          <td className="px-3 py-2 font-medium text-zinc-100">{formatMoney(payment.valor || payment.transaction_amount || 0, payment.moeda || moeda)}</td>
                          <td className={`px-3 py-2 font-semibold ${paymentTone[status] || 'text-zinc-300'}`}>{payment.status || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'equipe' && (
          <div className="space-y-2">
            {(client.equipe || []).length === 0 ? (
              <p className="rounded-lg bg-surface2 p-3 text-sm text-zinc-400">Nenhum usuário cadastrado nesta oficina.</p>
            ) : (
              client.equipe.map((member) => (
                <div key={member.id} className="flex items-center justify-between rounded-lg bg-surface2 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-zinc-100">{member.nome || '—'}</p>
                    <p className="truncate text-xs text-zinc-500">{member.email || '—'}</p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <span className="rounded-full bg-zinc-700/60 px-2 py-0.5 text-xs font-semibold text-zinc-300">{member.perfil || '—'}</span>
                    {!member.ativo && (
                      <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-semibold text-red-300">inativo</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {tab === 'venda' && (
          <div className="space-y-2">
            {(client.responsaveisVenda || []).length === 0 ? (
              <p className="rounded-lg bg-surface2 p-3 text-sm text-zinc-400">Nenhum responsável de venda/comissão vinculado.</p>
            ) : (
              client.responsaveisVenda.map((item) => (
                <div key={item.id} className="rounded-lg border border-border bg-surface2 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-zinc-100">{item.participant_nome || 'Parceiro'}</p>
                    <span className="rounded-full border border-orange-500/30 bg-orange-500/15 px-2 py-0.5 text-xs font-semibold text-orange-300">
                      {Number(item.percentual || 0) > 0 ? `${item.percentual}%` : ''}
                      {Number(item.valor_fixo || 0) > 0 ? ` + ${formatMoney(item.valor_fixo, moeda)}` : ''}
                    </span>
                  </div>
                  <div className="mt-1.5 grid gap-1 text-xs text-zinc-400 sm:grid-cols-2">
                    <span>Pagamentos gerados: <strong className="text-zinc-200">{item.pagamentos_gerados || 0}/{item.max_pagamentos || 0}</strong></span>
                    <span>Teto: <strong className="text-zinc-200">{item.teto_percentual_mensalidade || 0}%</strong> da mensalidade</span>
                    <span>Início: {formatDateBR(item.inicio_em)}</span>
                    <span>Fim: {formatDateBR(item.fim_em)}</span>
                    <span className="sm:col-span-2">Base de cálculo: {baseCalculoLabels[item.base_calculo] || item.base_calculo || '—'}</span>
                    {item.observacao ? <span className="italic text-zinc-500 sm:col-span-2">{item.observacao}</span> : null}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={() => { setTab('geral'); onClose?.(); }}>
            <X size={15} />
            Fechar
          </Button>
          {onEdit && (
            <Button onClick={() => onEdit(client)}>
              <Pencil size={15} />
              Editar cliente
            </Button>
          )}
        </div>


      </div>
    </Modal>
  );
}
